import React, { useState, useEffect } from 'react';

const COOKIE_CONSENT_KEY = 'cd:cookie-consent-v1';

export default function CookieBanner() {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) setHidden(true);
    } catch (_e) {}
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setHidden(true);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Reserve-space: keď je banner viditeľný, telo dostane triedu, ktorá pridá
  // spodný padding stránke (.cd-page) → obsah nikdy neskončí za fixným bannerom.
  useEffect(() => {
    if (!hidden) {
      document.body.classList.add('has-cookie-banner');
    } else {
      document.body.classList.remove('has-cookie-banner');
    }
    return () => document.body.classList.remove('has-cookie-banner');
  }, [hidden]);

  if (hidden) return null;

  // „Rozumiem" aj „Odmietnuť" zapíšu len voľbu, aby sa lišta znova neukazovala. Nič
  // nezapínajú: appka používa iba nevyhnutné technické úložisko. Chybové hlásenia
  // (Sentry) sa v prehliadači spustia len pri zázname `errorReports: true`, ktorý lišta
  // dnes nevie nastaviť (lib/observability.js hasConsent).
  const saveChoice = (choice) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
        choice,
        at: new Date().toISOString(),
      }));
    } catch (_e) {}
    setHidden(true);
  };
  const handleAccept = () => saveChoice('necessary-only');
  const handleReject = () => saveChoice('rejected');

  return (
    <div
      role="dialog"
      aria-label="Súkromie a úložisko"
      data-testid="cookie-banner"
      className="cd-cookie-banner"
      style={{
        position: 'fixed',
        bottom: 16, left: 16, right: 16,
        maxWidth: 880, margin: '0 auto',
        background: 'var(--bg-surface)',
        border: '1px solid var(--brand-green)',
        borderRadius: 'var(--r-lg)',
        padding: '14px 18px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
        zIndex: 1500,
        display: 'flex', alignItems: 'center', gap: 14,
        fontFamily: 'inherit',
      }}
    >
      {/* Desktop obsah — plné (právne) znenie + ikona + obe tlačidlá. Na mobile
          skrytý cez CSS (.cd-cookie-desktop { display:none }). */}
      <div className="cd-cookie-desktop" style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
        <div
          aria-hidden="true"
          className="cd-cookie-banner-icon"
          style={{
            width: 38, height: 38, borderRadius: '50%',
            background: 'var(--sec-yellow-soft)',
            flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="var(--sec-yellow)" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5z" />
            <circle cx="9" cy="11" r="0.6" fill="var(--sec-yellow)" stroke="none" />
            <circle cx="14" cy="14" r="0.6" fill="var(--sec-yellow)" stroke="none" />
            <circle cx="11" cy="16" r="0.6" fill="var(--sec-yellow)" stroke="none" />
          </svg>
        </div>

        <div className="cd-cookie-banner-body" style={{ flex: 1, minWidth: 0 }}>
          <div className="cd-cookie-banner-title" style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            Trochu súkromia, trochu pomoci
          </div>
          <div className="cd-cookie-banner-text" style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Používame iba nevyhnutné technické úložisko, ktoré appka potrebuje na fungovanie
            (prihlásenie, téma, nastavenia). Nič iné sa nezapína.
            {' '}
            <a href="/privacy" style={{ color: 'var(--product-primary-hover)', textDecoration: 'underline' }}>
              Zásady ochrany osobných údajov
            </a>.
          </div>
        </div>

        <div className="cd-cookie-banner-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleReject}
            data-testid="cookie-banner-reject"
            style={{
              padding: '8px 14px', borderRadius: 'var(--r-md)',
              border: '1px solid var(--border)', background: 'var(--bg-base)',
              color: 'var(--text-primary)', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Odmietnuť
          </button>
          <button
            type="button"
            onClick={handleAccept}
            data-testid="cookie-banner-accept"
            style={{
              padding: '8px 14px', borderRadius: 'var(--r-md)',
              border: 'none', background: 'var(--product-primary)',
              color: '#fff', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Rozumiem
          </button>
        </div>
      </div>

      {/* Mobilný obsah — kompaktný (kratší) variant. Bez ikony; dva riadky textu
          + jedno OK tlačidlo. Prepínanie desktop/mobil rieši CSS @media, nie JS.
          Default (desktop) skrytý cez .cd-cookie-mobile { display:none }. */}
      <div className="cd-cookie-mobile">
        <div>
          <div className="cd-cookie-m-title">Iba nevyhnutné</div>
          <div className="cd-cookie-m-sub">
            Nič iné sa nezapína · <a href="/privacy" className="cd-cookie-detail">Zásady</a>
          </div>
        </div>
        <button
          type="button"
          onClick={handleReject}
          data-testid="cookie-banner-reject-mobile"
          className="cd-cookie-no"
        >
          Odmietnuť
        </button>
        <button
          type="button"
          onClick={handleAccept}
          data-testid="cookie-banner-accept-mobile"
          className="cd-cookie-ok"
        >
          OK
        </button>
      </div>
    </div>
  );
}
